import { useState } from 'react';
import { useAlert } from '@weave/alert-system';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FaxStatus, FaxItem } from './fax-test-section.types';
import { getLocationUUID } from '../../../../../helpers/utils';
import { REACT_APP_API_URL, isDevEnv } from '../../../../../config/app';

// Environment-aware hardcoded fax ID and location ID for testing
const DEV_FAX_ID = 'd7f4470f-741c-4378-84bb-5077292cc098';
const DEV_LOCATION_ID = '11d7c1c2-486e-44c5-bb79-a500b6db7314';

const PROD_FAX_ID = '26515e16-78b2-403d-9c17-0e867bbc71ee';
const PROD_LOCATION_ID = 'd322ef62-67ce-4d20-a4aa-0e2ff341ce97';

// Get the appropriate IDs based on environment
const getTestFaxConfig = () => {
  const isDev = isDevEnv();
  return {
    faxId: isDev ? DEV_FAX_ID : PROD_FAX_ID,
    locationId: isDev ? DEV_LOCATION_ID : PROD_LOCATION_ID,
  };
};

// Get authentication headers from localStorage (same pattern as CustomAxios)
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token'); // AuthStorage.weave_token = 'token'
  const locationId = getLocationUUID(window.location.pathname);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (locationId) {
    headers['Location-Id'] = locationId;
  }

  return headers;
};

// Helper function to make API requests with proper auth
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  // Remove trailing slash from baseUrl if present
  const baseUrl = (REACT_APP_API_URL || '').replace(/\/$/, '');
  const authHeaders = getAuthHeaders();

  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...options,
    headers: {
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error?.message || errorData?.message || `API request failed: ${response.statusText}`);
  }

  return response.json();
};

// Query keys
const faxQueryKeys = {
  sentFaxes: (locationId: string, pageSize: number) => ['faxes', 'sent', locationId, pageSize],
  receivedFaxes: (locationId: string, pageSize: number) => ['faxes', 'received', locationId, pageSize],
  faxStatus: (faxId: string) => ['faxStatus', faxId],
};

// API functions
const fetchFaxes = async (locationId: string, direction: 'OUTBOUND' | 'INBOUND', pageSize: number): Promise<FaxItem[]> => {
  const params = new URLSearchParams({
    locationId,
    locationIds: locationId,
    direction,
    limit: String(pageSize),
  });
  const response = await apiRequest<{ rows?: FaxItem[] }>(`/fax/v1/faxes?${params}`);
  return response?.rows || [];
};

const fetchFaxStatus = async (faxId: string, locationId: string): Promise<FaxStatus> => {
  const params = new URLSearchParams({ location_id: locationId });
  const response = await apiRequest<{ data?: FaxStatus } | FaxStatus>(`/fax/v1/faxes/${faxId}?${params}`);
  return (response as { data?: FaxStatus })?.data || response as FaxStatus;
};

// Send test fax using the new support API
const sendSupportTestFax = async (faxNumber: string): Promise<string | undefined> => {
  const config = getTestFaxConfig();
  const cleanFaxNumber = faxNumber.replace(/\D/g, '');

  const response = await apiRequest<{ faxId?: string; id?: string }>('/fax/v1/support/test-fax', {
    method: 'POST',
    body: JSON.stringify({
      contactNumber: cleanFaxNumber,
      locationId: config.locationId,
      existingFaxId: config.faxId,
    }),
  });

  return response?.faxId || response?.id;
};

export const useFaxTestActions = (currentLocationId: string) => {
  const alerts = useAlert();
  const queryClient = useQueryClient();

  // Send Test Fax state
  const [resultingFaxId, setResultingFaxId] = useState('');
  const [showResultModal, setShowResultModal] = useState(false);
  const [sentPageSize, setSentPageSize] = useState<number>(5);
  const [receivedPageSize, setReceivedPageSize] = useState<number>(5);

  // Check Fax Status state
  const [faxStatus, setFaxStatus] = useState<FaxStatus | null>(null);
  const [checkStatusFaxId, setCheckStatusFaxId] = useState<string>('');

  // Query for last sent faxes
  const {
    data: lastSentFaxes = [],
    isLoading: lastSentFaxesLoading,
    refetch: refetchSentFaxes,
  } = useQuery(
    faxQueryKeys.sentFaxes(currentLocationId, sentPageSize),
    () => fetchFaxes(currentLocationId, 'OUTBOUND', sentPageSize),
    {
      enabled: !!currentLocationId,
      refetchOnWindowFocus: false,
      onError: (error: Error) => {
        console.error('Failed to fetch last sent faxes:', error);
      },
    }
  );

  // Query for last received faxes
  const {
    data: lastReceivedFaxes = [],
    isLoading: lastReceivedFaxesLoading,
    refetch: refetchReceivedFaxes,
  } = useQuery(
    faxQueryKeys.receivedFaxes(currentLocationId, receivedPageSize),
    () => fetchFaxes(currentLocationId, 'INBOUND', receivedPageSize),
    {
      enabled: !!currentLocationId,
      refetchOnWindowFocus: false,
      onError: (error: Error) => {
        console.error('Failed to fetch last received faxes:', error);
      },
    }
  );

  // Query for fax status (manually triggered)
  const {
    isLoading: checkStatusLoading,
    refetch: refetchFaxStatus,
  } = useQuery(
    faxQueryKeys.faxStatus(checkStatusFaxId),
    () => fetchFaxStatus(checkStatusFaxId, currentLocationId),
    {
      enabled: false, // Manual trigger only
      refetchOnWindowFocus: false,
      onSuccess: (data: FaxStatus) => {
        setFaxStatus(data);
        alerts.success('Fax status retrieved');
      },
      onError: (error: Error) => {
        const errorMessage = error.message || 'Failed to get fax status';
        alerts.error(errorMessage);
        setFaxStatus({ error: errorMessage });
      },
    }
  );

  // Mutation for sending test fax using the new support API
  const sendFaxMutation = useMutation(
    async (faxNumber: string) => {
      const newFaxId = await sendSupportTestFax(faxNumber);
      return newFaxId;
    },
    {
      onSuccess: (newFaxId: string | undefined) => {
        if (newFaxId) {
          setResultingFaxId(newFaxId);
          setShowResultModal(true);
          alerts.success('Test fax sent successfully!');
          // Invalidate sent faxes query to refetch
          queryClient.invalidateQueries(faxQueryKeys.sentFaxes(currentLocationId, sentPageSize));
        } else {
          alerts.success('Fax forwarded, but no fax ID was returned');
        }
      },
      onError: (error: Error) => {
        const errorMessage = error.message || 'Failed to send test fax';
        alerts.error(errorMessage);
      },
    }
  );

  // Check fax status
  const checkFaxStatus = async (faxId: string) => {
    if (!faxId) {
      alerts.error('Please provide a Fax ID to check');
      return;
    }
    setCheckStatusFaxId(faxId);
    setFaxStatus(null);
    // Trigger the query after setting the faxId
    setTimeout(() => refetchFaxStatus(), 0);
  };

  // Send test fax handler
  const createContactAndSendFax = async (faxNumber: string, onSuccess?: (faxId: string) => void) => {
    if (!faxNumber) {
      alerts.error('Please provide a Fax Number (recipient)');
      return;
    }
    sendFaxMutation.mutate(faxNumber, {
      onSuccess: (newFaxId) => {
        if (newFaxId && onSuccess) {
          onSuccess(newFaxId);
        }
      },
    });
  };

  // Fetch functions for manual refresh with new page size
  const fetchLastSentFaxes = (pageSize?: number) => {
    if (pageSize !== undefined && pageSize !== sentPageSize) {
      setSentPageSize(pageSize);
    }
    refetchSentFaxes();
  };

  const fetchLastReceivedFaxes = (pageSize?: number) => {
    if (pageSize !== undefined && pageSize !== receivedPageSize) {
      setReceivedPageSize(pageSize);
    }
    refetchReceivedFaxes();
  };

  // Copy to clipboard helper
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alerts.success('Copied to clipboard!');
  };

  return {
    // Send Test Fax
    sendTestFaxLoading: sendFaxMutation.isLoading,
    resultingFaxId,
    showResultModal,
    setShowResultModal,
    createContactAndSendFax,

    // Check Fax Status
    faxStatus,
    checkStatusLoading,
    checkFaxStatus,

    // Last Sent Faxes
    lastSentFaxes,
    lastSentFaxesLoading,
    sentPageSize,
    setSentPageSize,
    fetchLastSentFaxes,

    // Last Received Faxes
    lastReceivedFaxes,
    lastReceivedFaxesLoading,
    receivedPageSize,
    setReceivedPageSize,
    fetchLastReceivedFaxes,

    // Utilities
    copyToClipboard,
  };
};
